/**
 * The Tree-Table's VIEW STATE contract — what may be hidden, and how a saved view is
 * written down (DM W1 W4, worklist 3.7 + İ7).
 *
 * ── IT LIVES IN `shared` BECAUSE THE STORE IS OPAQUE ───────────────────────
 * `pm_user_view_pref` stores the document verbatim and reads no field of it
 * (`pmUserViewPref.schemas.ts`), so the ONLY authority over its shape is this module. It
 * is not a second server-side parse: no API code imports it, and the door's `z.unknown()`
 * is unchanged. What `shared` buys over a web-local copy is that the api package can pin
 * the two cross-package invariants — the minted key satisfies the door's charset, and the
 * hidden CLASSES that name a lifecycle state really are states the type package ships.
 *
 * ── THE VERSION IS A REFUSAL, NOT A HINT ──────────────────────────────────
 * A document whose `version` is newer than {@link DM_TREE_VIEW_PREF_VERSION} is refused
 * whole. A fail-soft parse would restore a SUBSET of somebody's saved view and call it
 * restored, which is the failure the door's byte bound is written for one layer down.
 * Any change to what a field MEANS — a new hide class included — bumps the version.
 */
import { z } from 'zod';
/**
 * Hide classes that name a lifecycle STATE, and are matched against `item.status`.
 *
 * Pinned as a subset of `DM_LIFECYCLE_STATE_VALUES` in this module's suite: a class naming
 * a state the package does not ship would be a chip that can never hide a row.
 */
export const DM_TREE_HIDE_STATUS_CLASSES = ['done', 'abandoned', 'archived', 'paused'];
/**
 * Every class a chip may hide, in the order the chips are drawn.
 *
 * `sprints` is NOT a status and not a tree row either — a sprint is a `member_of` edge and
 * never a node of the containment tree (SRS:1377), so the class removes the MEMBERSHIP
 * SECTION drawn beneath the table. Spelled here rather than beside the chips so the
 * project-default setting and the stored documents share one vocabulary.
 */
export const DM_TREE_HIDE_CLASSES = ['sprints', ...DM_TREE_HIDE_STATUS_CLASSES];
/** The `sprints` class, as the one consumer that is not a status test names it. */
export const DM_TREE_HIDE_SPRINTS_CLASS = 'sprints';
/** The newest shape version this build reads and writes (2: `paused` hide class, W5-0b). */
export const DM_TREE_VIEW_PREF_VERSION = 2;
/**
 * Versions this build READS. v1 is v2 minus `paused`, so the v2 body schema reads it as is;
 * a v1-only build reading a v2 document answers `newer` and leaves it alone.
 */
const dmTreeViewPrefVersionSchema = z.union([z.literal(1), z.literal(DM_TREE_VIEW_PREF_VERSION)]);
/** The classes a v1 document cannot name. */
const DM_TREE_V2_HIDE_CLASSES = ['paused'];
/**
 * The LOWEST version that expresses `body` — the one a document is written at, so a rolled-back
 * build still reads (and writes) every view that hides no v2 class (review c5173 #5).
 */
function dmTreeFilterBodyVersion(body) {
    return body.hidden.some((cls) => DM_TREE_V2_HIDE_CLASSES.includes(cls)) ? DM_TREE_VIEW_PREF_VERSION : 1;
}
const VERSION_TOO_LOW = { message: 'the document names a hide class its version cannot express' };
/** The view-preference key holding the filters in force. */
export const DM_TREE_FILTERS_PREF_KEY = 'tree.filters';
/** Saved views live under this prefix, one key each. */
export const DM_TREE_PRESET_KEY_PREFIX = 'tree.preset.';
/**
 * The slug bound, well under the column's 120 once the prefix is paid for. The api suite
 * pins prefix + this against `PM_USER_VIEW_PREF_KEY_MAX_LENGTH`; two different names must
 * never truncate into one row.
 */
export const DM_TREE_PRESET_SLUG_MAX_LENGTH = 80;
/** What a preset may be CALLED on screen; the slug is derived from it, not typed. */
export const DM_TREE_PRESET_NAME_MAX_LENGTH = 60;
/** The muted set and the chips — the half a preset stores and the filters key repeats. */
export const dmTreeFilterBodySchema = z
    .object({
    hidden: z.array(z.enum(DM_TREE_HIDE_CLASSES)),
    /**
     * Muted items as `item_no`s, the alphabet `?isolate=`/`?focus=` speak and the one an
     * operator reads off a row ([[feedback_id_namespaces]]). A `pm_item.id` here would
     * make the document unreadable beside every other DM wire format.
     */
    muted: z.array(z.number().int().positive()),
})
    .strict();
export const dmTreeFiltersDocumentSchema = dmTreeFilterBodySchema
    .extend({ version: dmTreeViewPrefVersionSchema })
    .strict()
    .refine((doc) => doc.version >= dmTreeFilterBodyVersion(doc), VERSION_TOO_LOW);
export const dmTreePresetDocumentSchema = z
    .object({
    version: dmTreeViewPrefVersionSchema,
    name: z.string().min(1).max(DM_TREE_PRESET_NAME_MAX_LENGTH),
    filters: dmTreeFilterBodySchema,
})
    .strict()
    .refine((doc) => doc.version >= dmTreeFilterBodyVersion(doc.filters), VERSION_TOO_LOW);
/** `version` as a number, or `null` when the document does not carry one. */
function documentVersion(value) {
    if (typeof value !== 'object' || value === null || Array.isArray(value))
        return null;
    const version = value.version;
    return typeof version === 'number' && Number.isFinite(version) ? version : null;
}
function parseDocument(schema, value) {
    const parsed = schema.safeParse(value);
    if (parsed.success)
        return { status: 'ok', value: parsed.data };
    const version = documentVersion(value);
    if (version !== null && version > DM_TREE_VIEW_PREF_VERSION)
        return { status: 'newer', version };
    return { status: 'unreadable' };
}
export function parseDmTreeFiltersDocument(value) {
    const parsed = parseDocument(dmTreeFiltersDocumentSchema, value);
    if (parsed.status !== 'ok')
        return parsed;
    const { hidden, muted } = parsed.value;
    return { status: 'ok', value: { hidden, muted } };
}
export function parseDmTreePresetDocument(value) {
    const parsed = parseDocument(dmTreePresetDocumentSchema, value);
    if (parsed.status !== 'ok')
        return parsed;
    return { status: 'ok', value: { name: parsed.value.name, filters: parsed.value.filters } };
}
export function dmTreeFiltersDocument(body) {
    return { version: dmTreeFilterBodyVersion(body), hidden: body.hidden, muted: body.muted };
}
export function dmTreePresetDocument(preset) {
    return { version: dmTreeFilterBodyVersion(preset.filters), name: preset.name, filters: preset.filters };
}
/**
 * A display name → the slug half of its key, or `null` when nothing survives.
 *
 * The key is an ADDRESS and the name is text: the door's charset admits neither spaces nor
 * case, and a renamed preset must keep its row rather than mint a second one.
 */
export function dmTreePresetSlug(name) {
    const slug = name
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, DM_TREE_PRESET_SLUG_MAX_LENGTH)
        // A trailing dash can only appear after the slice, and it is legal in the charset;
        // trimmed anyway so `"a b"` and `"a b!"` cannot address two rows.
        .replace(/-+$/g, '');
    return slug === '' ? null : slug;
}
/** The full view-preference key for a preset slug. */
export function dmTreePresetKey(slug) {
    return `${DM_TREE_PRESET_KEY_PREFIX}${slug}`;
}
export function isDmTreePresetKey(key) {
    return key.startsWith(DM_TREE_PRESET_KEY_PREFIX) && key.length > DM_TREE_PRESET_KEY_PREFIX.length;
}
//# sourceMappingURL=pmTreeView.js.map